import { type ComponentProps, useState } from 'react';
import { TextField } from './TextField';

type BaseProps = Omit<ComponentProps<typeof TextField>, 'type' | 'value' | 'onChange'>;

type NumberFieldProps = BaseProps &
  (
    | { nullable?: false | undefined; value: number; onChange: (value: number) => void }
    | { nullable: true; value: number | null | undefined; onChange: (value: number | null) => void }
  );

/**
 * A numeric input that edits as text.
 *
 * Binding `<input type="number">` straight to a number cannot be edited
 * cleanly: a field showing `0` becomes `05` when the user types a 5 (React
 * leaves the DOM alone because `"05" == 5`), and clearing it gives back
 * `Number('') === 0`, so the 0 returns right away. While the field has focus it
 * shows what was typed instead. A 0 is cleared on focus. On blur the field
 * shows the stored value again.
 *
 * A blank field is reported as `0`, or as `null` when `nullable` is set.
 */
export function NumberField(props: NumberFieldProps) {
  const { nullable, value, onChange, onFocus, onBlur, ...rest } = props;
  const [draft, setDraft] = useState<string | null>(null);
  const wholeSteps = rest.step === undefined || Number.isInteger(Number(rest.step));

  return (
    <TextField
      inputMode={wholeSteps ? 'numeric' : 'decimal'}
      placeholder={nullable ? undefined : '0'}
      {...rest}
      type="number"
      value={draft ?? (value == null ? '' : String(value))}
      onFocus={(event) => {
        setDraft(value == null || value === 0 ? '' : String(value));
        onFocus?.(event);
      }}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        if (raw === '') {
          if (props.nullable) props.onChange(null);
          else props.onChange(0);
          return;
        }
        const next = Number(raw);
        if (Number.isFinite(next)) onChange(next);
      }}
      onBlur={(event) => {
        setDraft(null);
        onBlur?.(event);
      }}
    />
  );
}
